import AuthApiService from '@package/core/src/auth/AuthApiService';
import AuthInteractor from '@package/core/src/auth/AuthInteractor';
import BullseyeBucksApiService from '@package/core/src/bullseyeBucks/BullseyeBucksApiService';
import BullseyeBucksInteractor from '@package/core/src/bullseyeBucks/BullseyeBucksInteractor';
import { RedeemLoyaltyPoints } from '@package/core/src/bullseyeBucks/types';
import { ApiError } from '@package/services/src/ApiClient';

import { apiClient } from '../services';
import { CHEKOUT_SUMMARY_DATA_QUERY_KEY } from '../useCheckoutSummary/types';
import { useQueryClient, useStrictMutation, useStrictQuery } from '../useCustomQuery';
import { GIFT_CARD_QUERY_KEY } from '../useGiftCard/types';
import { PAYMENT_METHODS_QUERY_KEY } from '../usePaymentMethods/types';
import { SHOPPING_CART_DATA_QUERY_KEY } from '../useShoppingCart/types';

import { LOYALTY_POINTS_QUERY_KEY } from './types';

const bullseyeBucksCardService = new BullseyeBucksApiService(apiClient);
const bullseyeBucksInteractor = new BullseyeBucksInteractor(bullseyeBucksCardService);

const authService = new AuthApiService(apiClient);
const authInteractor = new AuthInteractor(authService);

const useBullseyeBucks = () => {
  const queryClient = useQueryClient();

  const { data } = useStrictQuery(
    [LOYALTY_POINTS_QUERY_KEY],
    async () => {
      const user = await authInteractor.getUser();

      if (user.data) {
        const res = await bullseyeBucksInteractor.getLoyaltyPoints();
        return res.data;
      }

      return { earnedPoints: 0, redemptionAmounts: [] };
    },
    { refetchOnWindowFocus: false },
  );

  const {
    data: redemptionPoint,
    mutateAsync: redeemLoyaltyPoints,
    error,
  } = useStrictMutation(
    async (data: RedeemLoyaltyPoints) => {
      const res = await bullseyeBucksInteractor.redeemLoyaltyPoints(data);

      return res.data;
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([LOYALTY_POINTS_QUERY_KEY]);
        void queryClient.invalidateQueries([GIFT_CARD_QUERY_KEY]);
        void queryClient.invalidateQueries([SHOPPING_CART_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([CHEKOUT_SUMMARY_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([PAYMENT_METHODS_QUERY_KEY]);
      },
    },
  );

  return {
    data,
    redemptionPoint,
    error: error as ApiError | undefined,
    redeemLoyaltyPoints,
  };
};

export default useBullseyeBucks;
